import { useEffect, useState } from "react";
import { Form, FormCheckbox, FormActions, FormInput, FormTextArea } from "../Controls/Form";
import UserData from "../Controls/UserData";
import EmployerSearch from "../Controls/EmployerSearch";
import Help from "../Controls/Help";
import { FaThumbsDown as ThumbsDown, FaThumbsUp as ThumbsUp, FaRegMeh as Meh } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAccountProfileQuery, refreshAccountProfile } from "../../query/useAccountProfile";
import { useQueryClient } from "@tanstack/react-query";

function emptyPrefs() {
  return {
    ID: 0,
    EmployerName: "",
    EmployerID: 0,
    Reason: "",
    MinAnnualCompensation: null,
    MinTravelPercent: 0,
    AnyLocation: false,
    Active: true,
    PreferenceTypeID: 2,
  };
}

/**
 * Employer preference form body. Prefer EmployerPrefEditModal for UI entry points.
 */
export default function EmployerPrefEdit({ preferenceId = 0, onClose }) {
  const id = Number.parseInt(preferenceId, 10) || 0;
  const queryClient = useQueryClient();
  const { data: profile } = useAccountProfileQuery();
  const [prefs, updatePrefs] = useState(() => emptyPrefs());

  useEffect(() => {
    if (!id) {
      updatePrefs(emptyPrefs());
      return;
    }
    const found = (profile?.employerPreferences || []).find((e) => Number(e.ID || e.id) === id);
    if (found) updatePrefs({ ...found });
  }, [id, profile]);

  function close() {
    onClose?.();
  }

  function onChange(value, field) {
    if (field === "Employer") {
      const employerId = value?.ID ?? value?.id ?? 0;
      const employerName = value?.Name ?? value?.name ?? "";
      updatePrefs({ ...prefs, EmployerID: employerId, EmployerName: employerName });
    } else {
      updatePrefs({ ...prefs, [field]: value });
    }
  }

  async function onDoneAsync() {
    const employerId = prefs.EmployerID || prefs.employerID || 0;
    if (!employerId || employerId <= 0) {
      toast.error("Please select an employer");
      return;
    }

    const payload = {
      ...prefs,
      ID: prefs.ID || prefs.id || 0,
      EmployerID: employerId,
      EmployerName: prefs.EmployerName || prefs.employerName || "",
      MinAnnualCompensation: prefs.MinAnnualCompensation ?? prefs.minAnnualCompensation ?? null,
      MinTravelPercent: Number(prefs.MinTravelPercent ?? prefs.minTravelPercent ?? 0),
      AnyLocation: !!(prefs.AnyLocation ?? prefs.anyLocation),
      PreferenceTypeID: prefs.PreferenceTypeID ?? prefs.preferenceTypeID ?? 2,
      Reason: prefs.Reason || prefs.reason || "",
      Active: prefs.Active ?? prefs.active ?? true,
    };

    const resp = await UserData.UpdateAsync("employerPreferences", payload, true);
    if (resp && resp.ok) {
      await refreshAccountProfile(queryClient);
      toast.success("Employer preference saved");
      close();
    } else {
      toast.error(resp?.message || "Failed to save employer preference");
    }
  }

  async function onDelete() {
    const prefId = prefs.ID || prefs.id;
    if (!prefId) return;
    const resp = await UserData.DeleteAsync("employerPreferences", prefId);
    if (resp && resp.result) {
      toast.success("Employer preference deleted");
      await refreshAccountProfile(queryClient);
      close();
    }
  }

  const hasSavedId = (prefs.ID || prefs.id || 0) > 0;
  const preferenceTypeId = prefs.PreferenceTypeID || 0;

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={prefs} onUpdate={updatePrefs}>
        <div className="field">
          <EmployerSearch
            name="Employer"
            label="Employer"
            value={prefs.EmployerName}
            valueId={prefs.EmployerID}
            returnTo="/account"
            onSelect={(value) => {
              onChange(value, "Employer");
            }}
          />
        </div>
        <div className="field">
          {prefs.EmployerName ? (
            <label>
              Please select your level of interest in working for {prefs.EmployerName} in the future
            </label>
          ) : (
            <label>Please select your level of interest in working for this company in the future</label>
          )}
          <Help id="2133" />
          <ul className="interestLevel">
            <li key="1">
              <span
                title="I really want to work for this company more than most others"
                onClick={() => {
                  onChange(1, "PreferenceTypeID");
                }}
                className={preferenceTypeId === 1 ? "selected" : ""}
              >
                <ThumbsUp className="interestLevelIcon" id="thumbUp" size={1} />
              </span>
            </li>
            <li key="0">
              <span
                title="Meh - I'd like to work for them as much as most others"
                onClick={() => {
                  onChange(2, "PreferenceTypeID");
                }}
                className={preferenceTypeId === 2 ? "selected" : ""}
              >
                <Meh className="interestLevelIcon" id="meh" size={1} />
              </span>
            </li>
            <li key="2">
              <span
                title="No - I do not ever want to work for this company!"
                onClick={() => {
                  onChange(3, "PreferenceTypeID");
                }}
                className={preferenceTypeId === 3 ? "selected" : ""}
              >
                <ThumbsDown className="interestLevelIcon" id="thumbDown" size={1} />
              </span>
            </li>
          </ul>
        </div>
        <FormTextArea label="Reason" field="Reason" helpid="2135" />
        <FormInput
          label="Minimum Annual Compensation"
          field="MinAnnualCompensation"
          type="number"
          helpid="2134"
        />
        <FormInput
          label="Maximum Travel Percent"
          field="MinTravelPercent"
          type="number"
          helpid="2137"
        />
        <FormCheckbox label="Any location for this employer" field="AnyLocation" helpid="2138" />
        <FormCheckbox label="Active" field="Active" helpid="2136" />
        <FormActions
          onSaveAsync={async () => {
            await onDoneAsync();
          }}
          onCancelAsync={async () => {
            close();
          }}
          onDeleteAsync={
            hasSavedId
              ? async () => {
                  await onDelete();
                }
              : undefined
          }
        />
      </Form>
    </div>
  );
}
